import {
  Failure,
  getId,
  maybeAppendCopyToText,
  Success,
} from "@indietabletop/appkit";
import { sortBy, uniqBy } from "es-toolkit";
import type { Greathelm } from "../../types/lib/index.ts";
import { plural } from "../fmt";
import { Knight } from "./Knight";
import type { Ruleset } from "./Ruleset";
import { WeakMapCache } from "./WeakMapCache";

type RangeIssue<T> = {
  type: T;
  max: number;
  current: number;
};

export type WarbandIssue =
  | RangeIssue<"MAX_WORN_AND_USABLES_EXCEEDED">
  | RangeIssue<"MAX_CONSUMABLES_EXCEEDED">
  | RangeIssue<"MAX_KNIGHTS_EXCEEDED">
  | { type: "INVALID_KNIGHTS"; count: number };

export class Warband {
  ruleset: Ruleset;
  data: Greathelm.WarbandData;
  private cache: WeakMapCache;

  constructor(ruleset: Ruleset, data: Greathelm.WarbandData) {
    this.ruleset = ruleset;
    this.data = data;
    this.cache = new WeakMapCache();
  }

  get id() {
    return this.data.id;
  }

  get name() {
    return this.data.name || "Unnamed";
  }

  get buildType() {
    return "Quick Battle";
  }

  get summary() {
    return plural(
      this.knights.reduce((acc, k) => acc + k.count, 0),
      { one: "# knight", other: "# knights" },
    );
  }

  get knights() {
    return this.cache.get(this.data.knights, (arr) =>
      arr.map((data) => new Knight(this, data)),
    );
  }

  get totalKnights() {
    return this.knights.reduce((acc, k) => acc + k.count, 0);
  }

  get equipment() {
    const equipmentWithDuplicates = this.knights
      .flatMap((knight) => {
        return [
          knight.armor,
          ...knight.weapons,
          ...knight.wearables,
          ...knight.items,
          ...knight.shields,
          ...knight.upgrades,
        ];
      })
      .filter((lookup) => lookup.isSuccess)
      .map((lookup) => lookup.value);

    const unsorted = uniqBy(equipmentWithDuplicates, (item) => item.id);

    return sortBy(unsorted, ["name"]);
  }

  get totalWornAndUsablesCount() {
    return this.knights.reduce(
      (acc, k) => acc + k.count * (k.usablesCount + k.wearablesCount),
      0,
    );
  }

  get totalConsumablesCount() {
    return this.knights.reduce(
      (acc, k) => acc + k.count * k.consumablesCount,
      0,
    );
  }

  get issues() {
    const issues: WarbandIssue[] = [];

    const {
      totalKnights,
      totalWornAndUsablesCount,
      totalConsumablesCount,
      ruleset,
    } = this;

    if (totalKnights > ruleset.maxQuickBattleKnights) {
      issues.push({
        type: "MAX_KNIGHTS_EXCEEDED",
        max: ruleset.maxQuickBattleKnights,
        current: totalKnights,
      });
    }

    if (totalWornAndUsablesCount > ruleset.maxQuickBattleWornAndUsables) {
      issues.push({
        type: "MAX_WORN_AND_USABLES_EXCEEDED",
        max: ruleset.maxQuickBattleWornAndUsables,
        current: totalWornAndUsablesCount,
      });
    }

    if (totalConsumablesCount > ruleset.maxQuickBattleConsumables) {
      issues.push({
        type: "MAX_CONSUMABLES_EXCEEDED",
        max: ruleset.maxQuickBattleConsumables,
        current: totalConsumablesCount,
      });
    }

    const knightsWithIssues = this.knights.filter((k) => k.issues.length > 0);

    if (knightsWithIssues.length) {
      issues.push({
        type: "INVALID_KNIGHTS",
        count: knightsWithIssues.reduce((acc, k) => acc + k.count, 0),
      });
    }

    return issues;
  }

  getKnight(id: string) {
    const knight = this.knights.find((k) => k.id === id);

    if (!knight) {
      return new Failure({ type: "KNIGHT_NOT_FOUND" as const });
    }

    return new Success(knight);
  }

  addKnight(data?: Partial<Greathelm.KnightData>) {
    const knight = Knight.from(this, data);
    const cloned = this.clone({ knights: [...this.data.knights, knight.data] });
    return cloned.getKnight(knight.id).valueOrThrow();
  }

  removeKnight(id: string) {
    return this.clone({
      knights: this.data.knights.filter((k) => k.id !== id),
    });
  }

  /**
   * Creates a copy of this warband with new id, name, timestamps, and resetting
   * the snapshotId, but keeps all other data intact.
   *
   * This method should be used in actions like "duplicate" or when copying
   * a shared warband.
   */
  duplicate() {
    const now = Date.now();

    return this.clone({
      id: getId(),
      name: maybeAppendCopyToText(this.data.name),
      createdTs: now,
      updatedTs: now,
      snapshotId: null,
    });
  }

  clone(overrides?: Partial<Greathelm.WarbandData>) {
    return new Warband(this.ruleset, { ...this.data, ...overrides });
  }

  static from(ruleset: Ruleset, props: { name: string }) {
    const now = Date.now();

    return new Warband(ruleset, {
      id: getId(),
      name: props.name,
      rulesetVersion: ruleset.version,
      snapshotId: null,
      knights: [],
      createdTs: now,
      updatedTs: now,
      deleted: false,
    });
  }
}
